import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import styles from './Jobs.scss'
import { Key } from '../shared/Key'
import { ValuesForSet } from '../shared/ValuesForSet'
import { CircleSpinner } from 'common/spinners'
import { CTA } from 'common/styleguide/CTA'
import { Card } from 'common/styleguide/Card'
import { Popover } from 'common/styleguide/Popover'
import { Checkbox } from 'common/styleguide/Checkbox'
import { tmsJobActions, tmsJobSelectors } from 'data/resources/tmsJobs'
import { tmsKeyActions, tmsKeySelectors } from 'data/resources/tmsKeys'
import { tmsValueActions, tmsValueSelectors } from 'data/resources/tmsValues'
import { TmsKey } from 'data/records'
import { Map } from 'immutable'
import _ from 'lodash'

const projectToGithub = Map({
  ui: 'rwgps-ui',
  rails: 'ridewithgps',
  android: 'RWGPS-Android',
  ios: 'RWGPS-iOS'
})

export const _Jobs = ({
  tmsJobId,
  tmsJob,
  loadJob,
  deleteJob,
  loadKeys,
  keysForJob,
  keysForSet,
  valuesForKey,
  verifyKey
}) => {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!tmsJobId) return // nav back to index counts as id change
    setLoading(true)
    Promise.all([loadJob(), loadKeys()])
      .finally(() => setLoading(false))
  }, [tmsJobId])

  if (!tmsJobId) {
    return <p>&larr; pick one</p>
  }

  if (loading) {
    return <CircleSpinner centered size={40} />
  }

  if (!loading && keysForJob.size === 0) {
    return <p>there are no translation keys for this job</p>
  }

  const onCheckboxChange = (key) => {
    key.tmsSetValidated
      ? verifyKey(key.id, null, false)
      : verifyKey(key.id, key.tmsSetId, true)
  }

  return <div className={styles.jobs}>
    <JobPane job={tmsJob} deleteJob={deleteJob} />
    <div className={styles.keys}>
      {keysForJob.valueSeq().map(tmsKey => {
        return <ExpandableKeyPane
          key={tmsKey.id}
          tmsKey={tmsKey}
          keysForSet={keysForSet}
          tmsJob={tmsJob}
          valuesForKey={valuesForKey}
          verifyKey={verifyKey}
          onCheckboxChange={onCheckboxChange}
        />
      })}
    </div>
  </div>
}

const ExpandableKeyPane = ({
  tmsKey,
  keysForSet,
  tmsJob,
  valuesForKey,
  verifyKey,
  onCheckboxChange
}) => {
  const [expanded, setExpanded] = useState(false)
  const toggleExpanded = () => setExpanded(!expanded)
  const otherKeysForSet = keysForSet(tmsKey.tmsSetId).filter(k => k.id !== tmsKey.id)

  return <Card key={`${tmsKey.id}-${tmsKey.tmsSetValidated}`}>
    <div style={{ display: 'flex' }}>
      <Checkbox horizontal checked={tmsKey.tmsSetValidated} disabled={tmsJob.orders?.missing === 0} onChange={() => onCheckboxChange(tmsKey)} />
      <TranslationKeyPane
        tmsKey={tmsKey}
        valuesForKey={valuesForKey}
        verifyKey={verifyKey}
        expanded={expanded}
        toggleExpanded={toggleExpanded}
      />
    </div>

    <div style={{ marginLeft: '22px' }}>
      {otherKeysForSet.size > 0 && <div>
        other keys that share this set:
        {otherKeysForSet.valueSeq().map(k => <div key={k.id}>{k.key}: {k.value}</div>)}
      </div>}

      {expanded && <ValuesForSet tmsSetId={tmsKey.tmsSetId} />}
    </div>
  </Card>
}

const _TranslationKeyPane = ({
  tmsKey,
  valuesForKey,
  verifyKey,
  findKey,
  expanded,
  toggleExpanded,
  loadValues
}) => {
  const [resolving, setResolving] = useState(false)
  const showResolveLink = !_.some(valuesForKey(tmsKey).valueSeq().toJS(), v => v.value) && tmsKey.matchKeyIds.length > 0 && !tmsKey.tmsSetValidated
  return <div style={{ width: '100%' }}>
    <Key tmsKey={tmsKey} expanded={expanded} toggleExpanded={toggleExpanded} />
    {showResolveLink && <div className={styles.dupes}>
      <b>this key has dupes.</b> <a onClick={() => setResolving(!resolving)}>{resolving ? 'stop resolving' : 'resolve them'}</a>, or <a onClick={() => verifyKey(tmsKey.id, tmsKey.tmsSetId, true)}>translate anyway</a>
    </div>}

    {resolving && <div className={styles.resolutionPane}>
      {tmsKey.matchKeyIds.map(matchKeyId => <DupePanel
        key={matchKeyId}
        tmsKey={tmsKey}
        d={findKey(parseInt(matchKeyId, 10))}
        loadValues={loadValues}
        valuesForKey={valuesForKey}
        verifyKey={verifyKey} />)}
    </div>}
  </div>
}

const mapStateToTranslationKeyPaneProps = (state, ownProps) => {
  return {
    valuesForKey: key => tmsValueSelectors.byTmsSetId(state, key.tmsSetId),
    findKey: kId => tmsKeySelectors.find(state, kId)
  }
}

const TranslationKeyPane = connect(
  mapStateToTranslationKeyPaneProps,
  (dispatch, ownProps) => ({
    loadValues: tmsSetId => dispatch(tmsValueActions.list({ tmsSetId }))
  })
)(_TranslationKeyPane)

const DupePanel = ({ tmsKey, d, loadValues, valuesForKey, verifyKey }) => {
  const values = valuesForKey(d)
  const target = useRef()
  const [showVals, setShowVals] = useState(false)

  useEffect(() => {
    loadValues(d.tmsSetId)
  }, [d])

  return <div className={styles.dupe}>
    <div>key: {d.key}</div>
    <div>value: {d.value}</div>
    <div>description: {d.description}</div>
    <CTA margins={false} link ref={target} onClick={() => setShowVals(true)}>show values</CTA>
    {showVals && <Popover
      target={target.current}
      placement='bottom'
      maxWidth='500px'
      onRequestClose={() => setShowVals(false)}>
      {values.valueSeq().map(v => {
        return <div key={v.id}>{v.language}: {v.value ?? 'none'}</div>
      })}
    </Popover>}
    <div style={{ margin: 'auto auto 5px auto' }}><CTA secondary margins={false} onClick={() => verifyKey(tmsKey.id, d.tmsSetId, true)}>use this one</CTA></div>
  </div>
}

const JobPane = ({ job, deleteJob: deleteJobAction }) => {
  const deleteJob = () => {
    deleteJobAction().then(window.location = '/tms/jobs')
  }

  if (!job) {
    return null // we probably just deleted the job, no need to throw
  }

  return <Card className={styles.topPane}>
    <div className={styles.left}>
      <div><b>project:</b> {job.project}</div>
      <div><b>sha:</b> {job.sha}</div>
      <div><b>created:</b> {job.createdAt}</div>
      <div><b>updated:</b> {job.updatedAt}</div>
    </div>

    <div className={styles.toolbar}>
      {job.pullRequestId && <CTA className={styles.submitCTA} secondary href={`https://github.com/ridewithgps/${projectToGithub.get(job.project)}/pull/${job.pullRequestId}`}>go to pr</CTA>}
      <CTA className={styles.deleteCTA} secondary onClick={deleteJob}>delete job</CTA>
      <StatusCTA job={job} />
    </div>
  </Card>
}

const _StatusCTA = ({ job, keys, orders, createOrders, refreshJob }) => {
  const [loading, setLoading] = useState(false)
  const [showOrders, setShowOrders] = useState(false)
  const target = useRef()
  const validated = _.every(keys.valueSeq().toJS(), k => k.tmsSetValidated)
  let action, text

  if (orders.missing > 0) {
    action = () => {
      setLoading(true)
      createOrders().finally(() => {
        setLoading(false)
      })
    }
    text = 'submit to gengo'
  }

  if (orders.sending > 0) {
    text = <CircleSpinner size={20} />
  }

  if (orders.sent > 0) {
    action = () => {
      setLoading(true)
      refreshJob().finally(() => {
        setShowOrders(true)
        setLoading(false)
      })
    }
    text = 'check status'
  }

  if (!validated) {
    text = 'validate keys first'
  }

  return <>
    {showOrders && <Popover
      target={target.current}
      placement='bottom'
      maxWidth='500px'
      onRequestClose={() => setShowOrders(false)}>
      {Object.entries(orders).map(([k, v]) => <div>{k}: {v}</div>)}
    </Popover>}
    <CTA ref={target} disabled={!validated} margins={false} onClick={() => action()}>
      {loading ? <CircleSpinner size={14} /> : text ?? 'download'}
    </CTA>
  </>
}

const StatusCTA = connect(
  (state, ownProps) => {
    const { id } = ownProps.job
    return {
      keys: tmsKeySelectors.byTmsJobId(state, id),
      orders: tmsJobSelectors.orders(state, id)
    }
  },
  (dispatch, ownProps) => {
    const { id } = ownProps.job
    return {
      createOrders: () => dispatch(tmsJobActions.createOrders(id)),
      refreshJob: () => dispatch(tmsJobActions.read(id))
    }
  }
)(_StatusCTA)

const mapStateToProps = (state, ownProps) => {
  const tmsJobId = parseInt(ownProps.params?.id, 10)
  return {
    tmsJobId,
    tmsJob: tmsJobSelectors.find(state, tmsJobId),
    keysForJob: tmsKeySelectors.byTmsJobId(state, tmsJobId),
    keysForSet: tmsSetId => tmsKeySelectors.byTmsSetId(state, tmsSetId),
    valuesForKey: k => tmsValueSelectors.byTmsSetId(state, k.tmsSetId)
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const tmsJobId = parseInt(ownProps.params?.id, 10)
  return {
    loadJob: () => dispatch(tmsJobActions.read(tmsJobId)),
    deleteJob: () => dispatch(tmsJobActions.delete(tmsJobId)),
    loadKeys: () => dispatch(tmsKeyActions.list({ tmsJobId })),
    verifyKey: (keyId, tmsSetId, tmsSetValidated) => dispatch(tmsKeyActions.update(keyId, TmsKey.serialize(TmsKey({ tmsSetId, tmsSetValidated }), ['tmsSetId', 'tmsSetValidated'])))
  }
}

export const Jobs = connect(
  mapStateToProps,
  mapDispatchToProps
)(_Jobs)
